import { DataGrid } from '@mui/x-data-grid';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import {
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogTitle,
    IconButton,
    Menu,
    MenuItem
} from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import SkeletonEarningCard from '../../../component/cards/Skeleton/EarningCard';
import { useAlerts } from '../../../hooks/useAlerts';
import { ackAlert } from '../../../services/alerts';
import { fetchRecord } from '../../../services/records';

export const fetchRecordByUuid = async (uuid) => {
    const { data } = await fetchRecord(uuid);
    return data;
};

const RendEditCell = ({ row, type }) => {
    const alert = row;

    const navigate = useNavigate();
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState(null);
    const [confirmOpen, setConfirmOpen] = useState(false);

    const handleMenuOpen = (event) => {
        setAnchorEl(event.currentTarget);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
    };

    const handleSeeClick = async () => {
        setLoading(true);
        handleMenuClose();
        try {
            const record = await fetchRecordByUuid(alert.session);
            navigate('/users/user/record', { state: { record } });
        } catch (err) {
            console.error('Failed to load record:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleMarkAnalyzedClick = () => {
        handleMenuClose();
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        setLoading(true);
        setConfirmOpen(false);
        try {
            await ackAlert(alert.uuid);
        } catch (err) {
            console.error('Falha ao marcar alerta:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleCancel = () => {
        setConfirmOpen(false);
    };

    return loading ? (
        <CircularProgress size={24} />
    ) : (
        <>
            <IconButton onClick={handleMenuOpen} size="small">
                <MoreVertIcon />
            </IconButton>
            <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleMenuClose}>
                <MenuItem onClick={handleSeeClick}>{t('alerts.viewSession')}</MenuItem>
                {type === 0 && (
                    <MenuItem onClick={handleMarkAnalyzedClick}>
                        {t('alerts.markAsAnalyzed')}
                    </MenuItem>
                )}
            </Menu>

            <Dialog open={confirmOpen} onClose={handleCancel}>
                <DialogTitle>{t('alerts.confirmAnalyzeDialog')}</DialogTitle>
                <DialogActions>
                    <Button onClick={handleCancel}>{t('common.cancel')}</Button>
                    <Button onClick={handleConfirm} color="primary" autoFocus>
                        {t('common.confirm')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

function UserAlertGrid({ type, group }) {
    const ack_pending = type === 0;
    const { t } = useTranslation();

    const {
        data,
        isLoading,
        count,
        rowsPerPageOptions,
        paginationModel,
        setPaginationModel
    } = useAlerts(undefined, group, ack_pending);

    const getTableConfig = (type) => ({
        columns: [
            {
                field: 'patient',
                headerName: t('tableHeaders.patient'),
                flex: 1,
                valueGetter: (params) => {
                    const row = params?.row ?? params;
                    const patient = row?.patient ?? params?.value ?? row;
                    return patient?.name ?? '';
                }
            },
            {
                field: 'type',
                headerName: t('tableHeaders.type'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                renderCell: (params) => {
                    const rowType = params?.row?.type ?? params?.value;
                    const label = typeLabels[rowType] ?? rowType;

                    if (rowType === 'FALL') {
                        return (
                            <span
                                style={{
                                    backgroundColor: 'rgba(248, 72, 72, 0.9)',
                                    color: '#ffffff',
                                    padding: '8px 16px',
                                    borderRadius: '12px',
                                    fontWeight: 'bold',
                                    display: 'inline-block',
                                    minWidth: '300px',
                                    textAlign: 'center',
                                    lineHeight: '1.5',
                                    boxSizing: 'border-box'
                                }}
                            >
                                {label}
                            </span>
                        );
                    }

                    if (rowType === 'HR_OVER_LIMIT') {
                        return (
                            <span
                                style={{
                                    backgroundColor: 'rgba(248,72,72,0.7)',
                                    color: '#ffffff',
                                    padding: '8px 16px',
                                    borderRadius: '12px',
                                    fontWeight: 'bold',
                                    display: 'inline-block',
                                    minWidth: '300px',
                                    textAlign: 'center',
                                    lineHeight: '1.5',
                                    boxSizing: 'border-box'
                                }}
                            >
                                {label}
                            </span>
                        );
                    }

                    return (
                        <span
                            style={{
                                color: '#282828',
                                fontWeight: 'bold',
                                textAlign: 'center'
                            }}
                        >
                            {label}
                        </span>
                    );
                }
            },
            {
                field: 'date',
                headerName: t('tableHeaders.date'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                renderCell: (params) => {
                    const ts = params.row.ts;
                    if (!ts) return '';
                    return ts.split('T')[0];
                }
            },
            {
                field: 'time',
                headerName: t('tableHeaders.time'),
                headerAlign: 'center',
                align: 'center',
                flex: 1,
                renderCell: (params) => {
                    const ts = params.row.ts;
                    if (!ts) return '';
                    const afterT = ts.split('T')[1] ?? '';
                    return afterT.split('+')[0].split('.')[0];
                }
            },
            {
                field: 'edit',
                headerName: t('tableHeaders.actions'),
                headerAlign: 'center',
                align: 'center',
                width: 250,
                renderCell: (params) => <RendEditCell row={params.row} type={type} />
            }
        ]
    });

    const typeLabels = {
        HR_OVER_LIMIT: t('alerts.types.HR_OVER_LIMIT'),
        HR_LOW_EFFORT: t('alerts.types.HR_LOW_EFFORT'),
        FALL: t('alerts.types.FALL')
    };

    return (
        <div style={{ width: '100%' }}>
            {isLoading ? (
                <SkeletonEarningCard />
            ) : (
                <DataGrid
                    paginationMode="server"
                    style={{ width: '100%' }}
                    density="standard"
                    loading={isLoading}
                    rows={data}
                    getRowId={(row) => row.uuid}
                    columns={getTableConfig(type).columns}
                    pageSizeOptions={rowsPerPageOptions}
                    rowCount={count}
                    paginationModel={paginationModel}
                    onPaginationModelChange={setPaginationModel}
                />
            )}
        </div>
    );
}

UserAlertGrid.propTypes = {
    type: PropTypes.oneOf([0, 1]).isRequired,
    group: PropTypes.string
};

export default UserAlertGrid;
